import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dictionary from '../utils/dictionary';
import functions from '../utils/functions';
import { beautifyTable, restoreTable } from '../utils/script/customTables';

const ThemeContext = React.createContext();
export const ThemeConsumer = ThemeContext.Consumer;

export const ThemeProvider = (props) => {
  const [tableDetected, setTableDetected] = useState(false);
  const [tableRowCount, setTableRowCount] = useState(0);
  const [viewAsRole, setViewAsRole] = useState('Normal View');
  const [displayMessage, setDisplayMessage] = useState(false);

  const msgTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const observerRef = useRef(null);

  // Auto-hide transient messages
  useEffect(() => {
    if (!displayMessage) return;
    clearTimeout(msgTimerRef.current);
    msgTimerRef.current = setTimeout(() => setDisplayMessage(false), 36000);
    return () => clearTimeout(msgTimerRef.current);
  }, [displayMessage]);

  // Helper: read current datatable info
  const getTableInfo = useCallback(() => {
    const tables = document.getElementsByClassName('datatable');
    if (!tables || tables.length === 0) {
      return { exists: false, id: null, rows: 0, el: null };
    }
    const el = tables[0];
    const tbody = el.querySelector('tbody');
    const rows = tbody ? tbody.rows.length : 0;
    const id = el.getAttribute('id');
    return { exists: true, id, rows, el };
  }, []);

  // Detect .datatable and row changes using MutationObserver
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const applyDetection = () => {
      const info = getTableInfo();
      setTableDetected(info.exists);
      setTableRowCount(info.rows);
    };

    // Initial check
    applyDetection();

    // Observe DOM changes to catch when table mounts/updates
    if ('MutationObserver' in window) {
      observerRef.current?.disconnect();
      observerRef.current = new MutationObserver(() => {
        // Coalesce multiple mutations in rapid succession
        queueMicrotask(applyDetection);
      });

      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => observerRef.current?.disconnect();
    }

    // Fallback: lightweight polling if MutationObserver not available
    clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(applyDetection, dictionary?._uiRefreshRate || 1500);
    return () => clearInterval(pollTimerRef.current);
  }, [getTableInfo]);

  const refreshDatatable = useCallback(() => {
    if (tableRowCount <= 0) return;

    const tables = document.getElementsByClassName('datatable');
    if (!tables || tables.length === 0) return;

    const table = tables[0];
    const tableId = table.getAttribute('id');
    if (!tableId) return;

    const eformats = table.getAttribute('export-format');
    const messageTop = table.getAttribute('messageTop');
    const messageBottom = table.getAttribute('messageBottom');

    // Don’t mutate dictionary._styledExportBtnFormats; clone what we use
    let newFormats = false;
    if (eformats) {
      const styled = dictionary._styledExportBtnFormats || {};
      const requested = eformats.split(',').map((s) => s.trim());

      newFormats = requested.map((fmt) => {
        switch (fmt) {
          case 'copy':
          case 'excel':
          case 'csv':
          case 'print':
            return styled[fmt] ? { ...styled[fmt] } : null;
          case 'pdf': {
            const base = styled.pdf ? { ...styled.pdf } : {};
            // Customize without side effects
            return {
              ...base,
              title: 'Multiplex Limited',
              messageTop,
              messageBottom,
            };
          }
          default:
            return null;
        }
      }).filter(Boolean);
    }

    // Initialize once; otherwise restore (idempotent)
    if (document.getElementsByClassName('dataTables_wrapper').length <= 0) {
      beautifyTable(tableId, true, newFormats);
    } else {
      restoreTable(tableId);
    }
  }, [tableRowCount]);

  const contextValue = useMemo(
    () => ({
      tableDetected,
      viewAsRole,
      displayMessage,
      setDisplayMessage,
      refreshDatatable,
      setViewAsRole,
    }),
    [tableDetected, viewAsRole, displayMessage, refreshDatatable]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {props.children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
